import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBottomSheetViewport } from "@/hooks/useBottomSheetViewport";
import { X, Gift, Loader2 } from "lucide-react";
import { getWishItems, type Wishlist, type WishItem } from "@/services/wishlists";
import WishlistItemCard from "./WishlistItemCard";

interface Props {
  wishlist: Wishlist;
  onClose: () => void;
}

const WishlistBottomSheet = ({ wishlist, onClose }: Props) => {
  const [items, setItems] = useState<WishItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { bottomPx, availableHeight } = useBottomSheetViewport(8);

  useEffect(() => {
    (async () => {
      const { data } = await getWishItems(wishlist.id);
      setItems(data || []);
      setLoading(false);
    })();
  }, [wishlist.id]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        {/* Sheet */}
        <motion.div
          className="relative w-full max-w-lg bg-background rounded-t-2xl flex flex-col"
          style={{
            bottom: `${bottomPx}px`,
            maxHeight: availableHeight > 0 ? `${availableHeight}px` : "85vh",
            height: "85vh",
          }}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h3 className="text-base font-bold font-serif text-foreground truncate">
              {wishlist.title}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Gift size={20} className="text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">No wishes yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {items.map((item) => (
                  <WishlistItemCard
                    key={item.id}
                    item={item}
                    isOwner={false}
                    isAuthenticated={false}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WishlistBottomSheet;
